import {
  AddItemByAccountDto,
  AddItemByCouponDto,
  AddItemToCartDto,
  CartDiscounts,
  CartStep,
  CartType,
  ChangeCurrencyForCartDto,
  RemoveDiscountForCartDto,
  RemoveItemFromCartDto,
  RepayResponse,
  SubmitCartDto,
  UpdateDiscountForCartDto,
  UpdateTradingSystemDto,
  UseCreditsOnCartDto,
} from '@monorepo/types';
import CrudService from '../core/crud-service';

export default class CartService extends CrudService {
  static override route = 'cart';

  async getCart() {
    try {
      const response: { data: CartType } = await this.httpService.get(
        `${this.path}`,
      );

      return response.data;
    } catch (error) {
      console.error(`Failed getting cart`, error);
      throw error;
    }
  }

  async getCartDiscounts() {
    try {
      const response: { data: CartDiscounts } = await this.httpService.get(
        `${this.path}/discounts`,
      );

      return response.data;
    } catch (error) {
      console.error(`Failed getting cart`, error);
      throw error;
    }
  }

  async updateCartStep(cartStep: CartStep) {
    try {
      const response: { data: CartType } = await this.httpService.put(
        `${this.path}/step`,
        { cartStep },
      );

      return response.data;
    } catch (e) {
      console.error(`Failed updating cart step`, cartStep);
      throw e;
    }
  }

  async addToCart(payload: AddItemToCartDto) {
    try {
      const response: { data: CartType } = await this.httpService.put(
        `${this.path}/add-item`,
        payload,
      );

      return response.data;
    } catch (e) {
      console.error(`Failed adding to cart`, payload);
      throw e;
    }
  }

  async updateDiscount(payload: UpdateDiscountForCartDto) {
    try {
      const response: { data: CartType } = await this.httpService.put(
        `${this.path}/discount`,
        payload,
      );

      return response.data;
    } catch (e) {
      console.error(`Failed updating discount`, payload);
      throw e;
    }
  }

  async updateTradingSystem(payload: UpdateTradingSystemDto) {
    try {
      const response: { data: CartType } = await this.httpService.put(
        `${this.path}/update-trading-system`,
        payload,
      );
      return response.data;
    } catch (e) {
      console.error(`Failed updating trading system`, payload);
      throw e;
    }
  }

  async creditsOnCart(payload: UseCreditsOnCartDto) {
    try {
      const response: { data: CartType } = await this.httpService.put(
        `${this.path}/use-credits`,
        payload,
      );

      return response.data;
    } catch (e) {
      console.error(`Failed using credits on cart`, payload.useCredits);
      throw e;
    }
  }

  async removeDiscount(payload: RemoveDiscountForCartDto) {
    try {
      const response: { data: CartType } = await this.httpService.put(
        `${this.path}/remove-discount`,
        payload,
      );

      return response.data;
    } catch (e) {
      console.error(`Failed removing discount`);
      throw e;
    }
  }

  async changeCurrency(payload: ChangeCurrencyForCartDto) {
    try {
      const response: { data: CartType } = await this.httpService.put(
        `${this.path}/change-currency`,
        payload,
      );
      return response.data;
    } catch (e) {
      console.error(`Failed changing currency`, payload);
      throw e;
    }
  }

  async removeFromCart(payload: RemoveItemFromCartDto) {
    try {
      const response: { data: CartType } = await this.httpService.put(
        `${this.path}/remove-item`,
        payload,
      );

      return response.data;
    } catch (e) {
      console.error(`Failed adding to cart`, payload);
      throw e;
    }
  }

  async emptyCart() {
    try {
      const response: { data: CartType } = await this.httpService.put(
        `${this.path}/empty`,
      );

      return response.data;
    } catch (e) {
      console.error(`Failed emptying cart`);
      throw e;
    }
  }

  async submitCart(payload: SubmitCartDto) {
    try {
      const response: { data: { url: string } } = await this.httpService.post(
        `${this.path}/submit-cart`,
        payload,
      );

      return response.data;
    } catch (e) {
      console.error(`Failed submitting cart`, e);
      throw e;
    }
  }

  async repayPurchase(snapshotId: string): Promise<RepayResponse> {
    try {
      const response: { data: RepayResponse } = await this.httpService.post(
        `${this.path}/repay`,
        { snapshotId },
      );

      return response.data;
    } catch (e) {
      console.error(
        `Failed repaying purchase with snapshot id: ${snapshotId}`,
        e,
      );
      throw e;
    }
  }

  async addItemByCoupon(payload: AddItemByCouponDto) {
    try {
      const response: { data: CartType } = await this.httpService.put(
        `${this.path}/add-item-by-coupon`,
        payload,
      );

      return response.data;
    } catch (e) {
      console.error(`Failed adding item by coupon`, payload);
      throw e;
    }
  }

  async addItemByAccount(payload: AddItemByAccountDto) {
    try {
      const response: { data: CartType } = await this.httpService.put(
        `${this.path}/add-item-by-account`,
        payload,
      );

      return response.data;
    } catch (e) {
      console.error(`Failed adding item by account`, payload);
      throw e;
    }
  }
}
